/* Kurabiye Cookie SDK Styles */

/**
 * CSS Variables - Prefixed with 'kurabiye' to prevent host website conflicts
 * 
 * All variables use the --kurabiye- prefix to avoid collision with host site CSS.
 * Variables are scoped to SDK elements (#kurabiye-banner, #kurabiye-modal, etc.)
 * 
 * To customize SDK colors via CSS, target these specific IDs:
 * 
 * #kurabiye-banner,
 * #kurabiye-modal,
 * #kurabiye-backdrop,
 * #kurabiye-icon {
 *   --kurabiye-primary-color: #your-color;
 *   --kurabiye-bg-color: #your-bg;
 *   --kurabiye-text-color: #your-text;
 * }
 */

/* Base styles with scoped CSS variables */
#kurabiye-banner,
#kurabiye-modal,
#kurabiye-backdrop,
#kurabiye-icon {
  /* Primary colors - hover/active are auto-derived if not set */
  --kurabiye-primary-color: #3b82f6;
  --kurabiye-primary-hover-color: #2563eb;
  --kurabiye-primary-active-color: #1d4ed8;

  /* Secondary colors */
  --kurabiye-secondary-color: #6b7280;
  --kurabiye-secondary-hover-color: #4b5563;

  /* Background colors */
  --kurabiye-bg-color: #ffffff;
  --kurabiye-bg-hover-color: #f9fafb;
  --kurabiye-bg-active-color: #f3f4f6;

  /* Text colors */
  --kurabiye-text-color: #1f2937;
  --kurabiye-text-muted-color: #6b7280;

  /* Border colors */
  --kurabiye-border-color: #e5e7eb;
  --kurabiye-border-hover-color: #d1d5db;

  /* Typography */
  --kurabiye-font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  --kurabiye-border-radius: 8px;
  --kurabiye-font-size: 14px;

  /* Focus ring - for accessibility */
  --kurabiye-focus-ring-color: rgba(59, 130, 246, 0.5);
  --kurabiye-focus-ring-offset: 2px;
}

/* Base styles */
.kurabiye-banner,
.kurabiye-modal {
  font-family: var(--kurabiye-font-family);
  font-size: var(--kurabiye-font-size);
  line-height: 1.5;
  color: var(--kurabiye-text-color);
  box-sizing: border-box;
}

.kurabiye-banner *,
.kurabiye-modal * {
  box-sizing: inherit;
}

/* Banner styles */
.kurabiye-banner {
  position: fixed;
  background: var(--kurabiye-bg-color) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 2147483640; /* Near max 32-bit integer - ensures visibility over any site content */
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
  border-radius: var(--kurabiye-border-radius);
  opacity: 0;
}

/* Box layout - compact container in corners */
.kurabiye-banner.kurabiye-box {
  max-width: 400px;
}

/* Bar layout - full width centered */
.kurabiye-banner.kurabiye-bar {
  left: 50%;
  transform: translateX(-50%) translateY(100%);
  max-width: 90rem;
  width: calc(100% - 2rem);
  border-radius: 0;
}

.kurabiye-banner.kurabiye-bar.kurabiye-bottom {
  bottom: 0;
}

.kurabiye-banner.kurabiye-bar.kurabiye-top {
  top: 0;
  transform: translateX(-50%) translateY(-100%);
}

.kurabiye-banner.kurabiye-bar.kurabiye-show {
  transform: translateX(-50%) translateY(0);
  opacity: 1;
}

/* Box positions - bottom-left */
.kurabiye-banner.kurabiye-box.kurabiye-bottom-left {
  bottom: 20px;
  left: 20px;
  transform: translateY(150%);
}

.kurabiye-banner.kurabiye-box.kurabiye-bottom-left.kurabiye-show {
  transform: translateY(0);
  opacity: 1;
}

/* Box positions - bottom-right */
.kurabiye-banner.kurabiye-box.kurabiye-bottom-right {
  bottom: 20px;
  right: 20px;
  transform: translateY(150%);
}

.kurabiye-banner.kurabiye-box.kurabiye-bottom-right.kurabiye-show {
  transform: translateY(0);
  opacity: 1;
}

/* Box positions - top-left */
.kurabiye-banner.kurabiye-box.kurabiye-top-left {
  top: 20px;
  left: 20px;
  transform: translateY(-150%);
}

.kurabiye-banner.kurabiye-box.kurabiye-top-left.kurabiye-show {
  transform: translateY(0);
  opacity: 1;
}

/* Box positions - top-right */
.kurabiye-banner.kurabiye-box.kurabiye-top-right {
  top: 20px;
  right: 20px;
  transform: translateY(-150%);
}

.kurabiye-banner.kurabiye-box.kurabiye-top-right.kurabiye-show {
  transform: translateY(0);
  opacity: 1;
}

.kurabiye-banner-content {
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.kurabiye-banner.kurabiye-bar .kurabiye-banner-content {
  flex-direction: row;
}

.kurabiye-banner.kurabiye-box .kurabiye-banner-content {
  flex-direction: column;
  align-items: stretch;
}

.kurabiye-banner-text {
  flex: 1;
}

.kurabiye-banner-title {
  margin: 0 0 8px;
  font-size: 18px; /* Match CookieYes */
  font-weight: 700;
  line-height: 1.3;
  /* Prevent host site CSS from overriding text color */
  color: var(--kurabiye-text-color) !important;
  -webkit-text-fill-color: var(--kurabiye-text-color) !important;
}

.kurabiye-banner-description {
  margin: 0;
  color: var(--kurabiye-secondary-color) !important;
  -webkit-text-fill-color: var(--kurabiye-secondary-color) !important;
  font-size: 15px; /* Improved from 14px for accessibility */
  line-height: 1.6; /* Improved line height */
}

.kurabiye-privacy-link {
  color: var(--kurabiye-primary-color) !important;
  -webkit-text-fill-color: var(--kurabiye-primary-color) !important;
  text-decoration: none;
  margin-top: 8px;
  display: inline-block;
  font-size: 13px;
}

.kurabiye-privacy-link:hover {
  text-decoration: underline;
}

.kurabiye-banner-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.kurabiye-banner.kurabiye-box .kurabiye-banner-actions {
  width: 100%;
  margin-top: 4px;
}

/* Banner Close Button (X) - for GDPR banner (Italy Garante compliance)
 * This is optional and configured via showBannerCloseButton
 * Just closes the banner without recording consent
 */
.kurabiye-banner-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none !important;
  border: none;
  font-size: 20px;
  color: var(--kurabiye-secondary-color) !important;
  -webkit-text-fill-color: var(--kurabiye-secondary-color) !important;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  z-index: 1;
}

.kurabiye-banner-close-btn:hover {
  background: var(--kurabiye-bg-hover-color) !important;
}

.kurabiye-banner-close-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 var(--kurabiye-focus-ring-offset) var(--kurabiye-focus-ring-color);
}

/* Adjust banner content padding when close button is present */
.kurabiye-banner-content:has(.kurabiye-banner-close-btn) {
  padding-right: 40px;
}

/* Cookie Icon Button - to reopen banner */
.kurabiye-cookie-icon {
  position: fixed;
  width: 48px;
  height: 48px;
  background: var(--kurabiye-primary-color) !important;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition:
    transform 0.2s ease,
    opacity 0.2s ease,
    visibility 0.2s ease;
  z-index: 2147483639; /* Just below banner */
  opacity: 0;
  visibility: hidden; /* Prevents click interaction when hidden */
  pointer-events: none; /* Belt and suspenders - ensures no click events */
  transform: scale(0.8);
}

.kurabiye-cookie-icon.kurabiye-show {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: scale(1);
}

.kurabiye-cookie-icon:hover {
  transform: scale(1.1);
}

.kurabiye-cookie-icon:active {
  transform: scale(0.95);
}

/* Cookie icon positions */
.kurabiye-cookie-icon.kurabiye-bottom-left {
  bottom: 20px;
  left: 20px;
}

.kurabiye-cookie-icon.kurabiye-bottom-right {
  bottom: 20px;
  right: 20px;
}

.kurabiye-cookie-icon.kurabiye-top-left {
  top: 20px;
  left: 20px;
}

.kurabiye-cookie-icon.kurabiye-top-right {
  top: 20px;
  right: 20px;
}

.kurabiye-cookie-icon.kurabiye-bottom {
  bottom: 20px;
  left: 20px;
}

.kurabiye-cookie-icon.kurabiye-top {
  top: 20px;
  left: 20px;
}

.kurabiye-cookie-icon svg {
  width: 24px;
  height: 24px;
  fill: white;
}

/* Button styles
 * CRITICAL: All background properties MUST use !important to prevent host site CSS from
 * overriding SDK elements. Without !important, host sites can set button backgrounds to
 * white/transparent, making the white "Accept All" text invisible.
 * See: f2cd190, incident with askaichat.app Accept All button disappearing.
 * DO NOT remove !important from any background property in this file.
 */
.kurabiye-btn {
  padding: 10px 20px;
  border: none;
  border-radius: var(--kurabiye-border-radius);
  font-size: var(--kurabiye-font-size);
  font-weight: 500;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;
  white-space: nowrap;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.kurabiye-btn:active {
  transform: scale(0.98);
}

.kurabiye-btn-primary {
  background: var(--kurabiye-primary-color) !important;
  color: white !important;
  -webkit-text-fill-color: white !important;
}

.kurabiye-btn-primary:hover {
  background: var(--kurabiye-primary-hover-color) !important;
}

.kurabiye-btn-primary:active {
  background: var(--kurabiye-primary-active-color) !important;
}

.kurabiye-btn-primary:focus-visible {
  outline: none;
  box-shadow: 0 0 0 var(--kurabiye-focus-ring-offset) var(--kurabiye-focus-ring-color);
}

.kurabiye-btn-secondary {
  background: var(--kurabiye-bg-color) !important;
  color: var(--kurabiye-primary-color) !important;
  -webkit-text-fill-color: var(--kurabiye-primary-color) !important;
  border: 2px solid var(--kurabiye-primary-color); /* Thicker border for better visibility */
}

.kurabiye-btn-secondary:hover {
  background: var(--kurabiye-bg-hover-color) !important;
  border-color: var(--kurabiye-border-hover-color);
}

.kurabiye-btn-secondary:active {
  background: var(--kurabiye-bg-active-color) !important;
}

.kurabiye-btn-secondary:focus-visible {
  outline: none;
  box-shadow: 0 0 0 var(--kurabiye-focus-ring-offset) var(--kurabiye-focus-ring-color);
}

/* Modal styles - ONLY for settings/preferences */
.kurabiye-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5) !important;
  z-index: 2147483645; /* High z-index for backdrop */
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;
}

.kurabiye-backdrop.kurabiye-show {
  opacity: 1;
  visibility: visible;
}

.kurabiye-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.95);
  background: var(--kurabiye-bg-color) !important;
  border-radius: var(--kurabiye-border-radius);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  z-index: 2147483646; /* Highest z-index for modal */
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease,
    transform 0.3s ease;
  display: flex;
  flex-direction: column;
}

.kurabiye-modal.kurabiye-show {
  opacity: 1;
  visibility: visible;
  transform: translate(-50%, -50%) scale(1);
}

.kurabiye-modal-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}

.kurabiye-modal-header {
  padding: 20px;
  border-bottom: 1px solid var(--kurabiye-border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.kurabiye-modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  /* Prevent host site CSS from overriding text color */
  color: var(--kurabiye-text-color) !important;
  -webkit-text-fill-color: var(--kurabiye-text-color) !important;
}

.kurabiye-modal-close {
  background: none !important;
  border: none;
  font-size: 24px;
  color: var(--kurabiye-secondary-color) !important;
  -webkit-text-fill-color: var(--kurabiye-secondary-color) !important;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.kurabiye-modal-close:hover {
  background: var(--kurabiye-bg-hover-color) !important;
}

.kurabiye-modal-close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 var(--kurabiye-focus-ring-offset) var(--kurabiye-focus-ring-color);
}

.kurabiye-modal-body {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.kurabiye-modal-description {
  margin: 0 0 20px;
  color: var(--kurabiye-text-muted-color) !important;
  -webkit-text-fill-color: var(--kurabiye-text-muted-color) !important;
  font-size: 15px; /* Improved from 14px for accessibility */
  line-height: 1.6; /* Improved line height */
}

.kurabiye-categories {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.kurabiye-category {
  padding: 15px;
  background: var(--kurabiye-bg-hover-color) !important;
  border-radius: 8px;
  border: 1px solid var(--kurabiye-border-color);
}

.kurabiye-category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 12px;
  flex-wrap: nowrap;
  overflow: hidden;
}

.kurabiye-category-title {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* CRITICAL: Prevent host site CSS from overriding text color */
  color: var(--kurabiye-text-color) !important;
  -webkit-text-fill-color: var(--kurabiye-text-color) !important;
}

/* Extra specificity for category titles to prevent host site override */
#kurabiye-modal .kurabiye-category .kurabiye-category-title,
.kurabiye-modal .kurabiye-category .kurabiye-category-title {
  color: var(--kurabiye-text-color) !important;
  -webkit-text-fill-color: var(--kurabiye-text-color) !important;
}

.kurabiye-category-description {
  margin: 0;
  font-size: 14px; /* Improved from 13px for accessibility */
  color: var(--kurabiye-text-muted-color) !important;
  -webkit-text-fill-color: var(--kurabiye-text-muted-color) !important;
  line-height: 1.5; /* Improved line height */
}

/* Toggle switch */
.kurabiye-toggle {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
  flex-shrink: 0;
}

.kurabiye-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.kurabiye-toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: background-color 0.2s ease;
  border-radius: 24px;
}

.kurabiye-toggle-slider:before {
  position: absolute;
  content: '';
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: transform 0.2s ease;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.kurabiye-toggle input:checked + .kurabiye-toggle-slider {
  background-color: var(--kurabiye-primary-color);
}

.kurabiye-toggle input:checked + .kurabiye-toggle-slider:before {
  transform: translateX(24px);
}

.kurabiye-toggle input:disabled + .kurabiye-toggle-slider {
  background-color: #e5e7eb;
  cursor: not-allowed;
  opacity: 0.6;
}

.kurabiye-modal-footer {
  padding: 20px;
  border-top: 1px solid var(--kurabiye-border-color);
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-shrink: 0;
  background: var(--kurabiye-bg-color) !important;
  flex-wrap: wrap;
}

.kurabiye-modal-footer .kurabiye-btn {
  flex-shrink: 0;
}

/* Dark theme - Override CSS variables for dark mode */
.kurabiye-dark.kurabiye-banner,
.kurabiye-dark.kurabiye-modal {
  --kurabiye-bg-color: #1f2937;
  --kurabiye-bg-hover-color: #374151;
  --kurabiye-bg-active-color: #4b5563;
  --kurabiye-text-color: #f3f4f6;
  --kurabiye-text-muted-color: #9ca3af;
  --kurabiye-border-color: #374151;
  --kurabiye-border-hover-color: #4b5563;
  --kurabiye-secondary-color: #9ca3af;
  --kurabiye-secondary-hover-color: #d1d5db;

  background: var(--kurabiye-bg-color) !important;
  color: var(--kurabiye-text-color);
}

.kurabiye-dark .kurabiye-banner-description,
.kurabiye-dark .kurabiye-modal-description,
.kurabiye-dark .kurabiye-category-description {
  color: var(--kurabiye-text-muted-color);
}

.kurabiye-dark .kurabiye-modal-header,
.kurabiye-dark .kurabiye-modal-footer {
  border-color: var(--kurabiye-border-color);
}

.kurabiye-dark .kurabiye-category {
  background: #111827 !important;
  border-color: var(--kurabiye-border-color);
}

.kurabiye-dark .kurabiye-modal-close {
  color: var(--kurabiye-secondary-color);
}

.kurabiye-dark .kurabiye-modal-close:hover {
  background: var(--kurabiye-bg-hover-color) !important;
}

.kurabiye-dark .kurabiye-btn-secondary {
  background: var(--kurabiye-bg-hover-color) !important;
  color: var(--kurabiye-text-color);
  border-color: #4b5563;
}

.kurabiye-dark .kurabiye-btn-secondary:hover {
  background: var(--kurabiye-bg-active-color) !important;
}

.kurabiye-dark .kurabiye-btn-secondary:active {
  background: #6b7280 !important;
}

.kurabiye-dark .kurabiye-cookie-icon {
  background: var(--kurabiye-bg-hover-color) !important;
}

.kurabiye-dark .kurabiye-cookie-icon:hover {
  background: var(--kurabiye-bg-active-color) !important;
}

/* Responsive - Small desktop/tablet adjustments */
@media (max-width: 640px) {
  .kurabiye-modal-footer {
    justify-content: stretch;
  }

  .kurabiye-modal-footer .kurabiye-btn {
    flex: 1 1 auto;
    min-width: 100px;
  }
}

/* Responsive - Mobile optimizations */
@media (max-width: 768px) {
  /* Banner adjustments for mobile - TRUE full-width edge-to-edge like CookieYes */
  .kurabiye-banner.kurabiye-box,
  .kurabiye-banner.kurabiye-bar {
    width: 100%;
    max-width: 100%;
    left: 0;
    right: 0;
    bottom: 0;
    margin: 0;
    border-radius: 6px 6px 0 0; /* Rounded top corners only */
    transform: translateY(100%);
  }

  .kurabiye-banner.kurabiye-box.kurabiye-bottom-left,
  .kurabiye-banner.kurabiye-box.kurabiye-bottom-right,
  .kurabiye-banner.kurabiye-bar.kurabiye-bottom {
    left: 0;
    right: 0;
    bottom: 0;
    transform: translateY(100%);
  }

  .kurabiye-banner.kurabiye-box.kurabiye-top-left,
  .kurabiye-banner.kurabiye-box.kurabiye-top-right,
  .kurabiye-banner.kurabiye-bar.kurabiye-top {
    left: 0;
    right: 0;
    top: 0;
    bottom: auto;
    border-radius: 0 0 6px 6px; /* Rounded bottom corners for top position */
    transform: translateY(-100%);
  }

  .kurabiye-banner.kurabiye-box.kurabiye-show,
  .kurabiye-banner.kurabiye-bar.kurabiye-show {
    transform: translateY(0);
    opacity: 1;
  }

  /* Force column layout for ALL banners on mobile (including bar layout) */
  .kurabiye-banner-content {
    padding: 20px 24px; /* Match CookieYes padding */
    gap: 16px;
    flex-direction: column !important;
    align-items: stretch;
  }

  /* Force full width for ALL banner actions on mobile */
  .kurabiye-banner-actions {
    width: 100% !important;
    flex-direction: column;
    gap: 10px;
  }

  .kurabiye-btn {
    width: 100%;
    min-height: 44px; /* Match CookieYes 44px height */
    padding: 10px 20px;
    font-size: 15px; /* Improved accessibility */
  }

  .kurabiye-banner-title {
    font-size: 18px; /* Match CookieYes */
    font-weight: 700;
  }

  .kurabiye-banner-description {
    font-size: 15px; /* Improved from 14px for better mobile readability */
    line-height: 1.6;
  }

  /* Modal optimizations for mobile */
  .kurabiye-modal {
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .kurabiye-modal-header {
    padding: 20px 24px; /* Match CookieYes padding */
  }

  .kurabiye-modal-body {
    padding: 0 24px; /* Match CookieYes padding */
  }

  .kurabiye-modal-footer {
    padding: 20px 24px; /* Match CookieYes padding */
    flex-wrap: wrap;
  }

  .kurabiye-modal-footer .kurabiye-btn {
    flex: 1;
    min-width: 120px;
  }

  /* Modal text accessibility improvements */
  .kurabiye-modal-description,
  .kurabiye-category-description {
    font-size: 15px; /* Improved from 14px */
    line-height: 1.6;
  }

  /* Cookie icon on mobile - always bottom-right */
  .kurabiye-cookie-icon {
    bottom: 16px;
    right: 16px;
    left: auto;
    top: auto;
  }
}

/* Body classes */
body.kurabiye-modal-open {
  overflow: hidden;
}

/* Animations */
@keyframes cc-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes cc-slide-up {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes cc-slide-down {
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
}

/* iFrame Placeholder Styles - for blocked embeds */
.kurabiye-iframe-placeholder {
  /* Scoped CSS variables for iframe placeholders */
  --kurabiye-primary-color: #3b82f6;
  --kurabiye-primary-hover-color: #2563eb;
  --kurabiye-primary-active-color: #1d4ed8;
  --kurabiye-border-radius: 8px;
  --kurabiye-font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;

  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border: 2px dashed #d1d5db;
  border-radius: var(--kurabiye-border-radius);
  min-height: 200px;
  font-family: var(--kurabiye-font-family);
  overflow: hidden;
}

.kurabiye-placeholder-content {
  text-align: center;
  padding: 24px;
  max-width: 300px;
}

.kurabiye-placeholder-content svg {
  color: #9ca3af;
  margin-bottom: 16px;
}

.kurabiye-placeholder-content p {
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 16px 0;
}

.kurabiye-placeholder-button {
  background: var(--kurabiye-primary-color) !important;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: var(--kurabiye-border-radius);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.kurabiye-placeholder-button:hover {
  background: var(--kurabiye-primary-hover-color) !important;
}

.kurabiye-placeholder-button:active {
  background: var(--kurabiye-primary-active-color) !important;
}

/* Dark mode for placeholder */
.kurabiye-dark .kurabiye-iframe-placeholder,
[data-theme='dark'] .kurabiye-iframe-placeholder {
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  border-color: #374151;
}

.kurabiye-dark .kurabiye-placeholder-content svg,
[data-theme='dark'] .kurabiye-placeholder-content svg {
  color: #6b7280;
}

.kurabiye-dark .kurabiye-placeholder-content p,
[data-theme='dark'] .kurabiye-placeholder-content p {
  color: #9ca3af;
}

/* Cookie Details Accordion Styles */

/* Always Active badge for necessary cookies - Green text like CookieYes */
.kurabiye-always-active {
  font-size: 14px;
  font-weight: 600;
  color: #008000; /* Green - matches CookieYes */
  background: transparent;
  padding: 0;
  border-radius: 0;
  white-space: nowrap;
}

.kurabiye-dark .kurabiye-always-active {
  color: #22c55e; /* Lighter green for dark mode visibility */
  background: transparent;
}

/* Category title row with expand button */
.kurabiye-category-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

/* Expand/Collapse button */
.kurabiye-category-expand {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent !important;
  cursor: pointer;
  flex-shrink: 0;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  /* Prevent host site button styles from affecting color */
  color: var(--kurabiye-text-muted-color) !important;
}

.kurabiye-category-expand:hover {
  background: var(--kurabiye-bg-active-color) !important;
}

.kurabiye-category-expand:focus-visible {
  outline: none;
  box-shadow: 0 0 0 var(--kurabiye-focus-ring-offset) var(--kurabiye-focus-ring-color);
}

/* Expand icon (chevron) */
.kurabiye-expand-icon {
  display: block;
  width: 10px;
  height: 10px;
  border-right: 2px solid var(--kurabiye-text-muted-color);
  border-bottom: 2px solid var(--kurabiye-text-muted-color);
  transform: rotate(-45deg);
  transition: transform 0.2s ease;
}

.kurabiye-expanded .kurabiye-expand-icon {
  transform: rotate(45deg);
}

/* Title button (clickable area for accordion) */
.kurabiye-category-title-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: none;
  background: transparent !important;
  cursor: pointer;
  text-align: left;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  /* CRITICAL: Prevent host site button styles from overriding text color */
  color: var(--kurabiye-text-color) !important;
  -webkit-text-fill-color: var(--kurabiye-text-color) !important;
}

/* Extra specificity for button text to prevent host site override */
#kurabiye-modal .kurabiye-category-title-btn,
.kurabiye-modal .kurabiye-category-title-btn {
  color: var(--kurabiye-text-color) !important;
  -webkit-text-fill-color: var(--kurabiye-text-color) !important;
}

.kurabiye-category-expandable .kurabiye-category-title-btn:hover .kurabiye-category-title {
  color: var(--kurabiye-primary-color) !important;
  -webkit-text-fill-color: var(--kurabiye-primary-color) !important;
}

.kurabiye-category-title-btn:focus-visible {
  outline: none;
}

.kurabiye-category-title-btn:focus-visible .kurabiye-category-title {
  text-decoration: underline;
}

/* Cookie count badge */
.kurabiye-cookie-count {
  font-size: 11px;
  font-weight: 400;
  color: var(--kurabiye-text-muted-color) !important;
  -webkit-text-fill-color: var(--kurabiye-text-muted-color) !important;
  background: var(--kurabiye-bg-active-color) !important;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
}

.kurabiye-dark .kurabiye-cookie-count {
  background: var(--kurabiye-bg-hover-color) !important;
}

/* Cookie details container (hidden by default) */
.kurabiye-cookie-details {
  display: none;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--kurabiye-border-color);
}

.kurabiye-expanded .kurabiye-cookie-details {
  display: block;
}

/* Individual cookie item */
.kurabiye-cookie-item {
  margin-bottom: 12px;
}

.kurabiye-cookie-item:last-child {
  margin-bottom: 0;
}

/* Cookie details table (CookieYes-style) */
.kurabiye-cookie-details-table {
  list-style: none;
  margin: 0;
  padding: 0;
  background: var(--kurabiye-bg-color);
  border: 1px solid var(--kurabiye-border-color);
  border-radius: 6px;
  overflow: hidden;
}

.kurabiye-dark .kurabiye-cookie-details-table {
  background: var(--kurabiye-bg-hover-color);
}

.kurabiye-cookie-details-table li {
  display: flex;
  padding: 8px 12px;
  border-bottom: 1px solid var(--kurabiye-border-color);
  font-size: 13px;
  line-height: 1.4;
}

.kurabiye-cookie-details-table li:last-child {
  border-bottom: none;
}

.kurabiye-cookie-label {
  flex-shrink: 0;
  width: 90px;
  font-weight: 500;
  color: var(--kurabiye-text-color) !important;
  -webkit-text-fill-color: var(--kurabiye-text-color) !important;
}

.kurabiye-cookie-value {
  flex: 1;
  color: var(--kurabiye-text-muted-color) !important;
  -webkit-text-fill-color: var(--kurabiye-text-muted-color) !important;
  word-break: break-word;
}

/* Responsive adjustments for cookie details */
@media (max-width: 768px) {
  .kurabiye-cookie-details-table li {
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px;
  }

  .kurabiye-cookie-label {
    width: auto;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--kurabiye-text-muted-color);
  }

  .kurabiye-cookie-value {
    font-size: 13px;
    color: var(--kurabiye-text-color);
  }
}
